import { MultiSelect, Select, Switch, Text } from '@byecode/ui'
import { FilterSelectWay } from '@lighthouse/core'
import { dateRangeVariableSystemNameMap, ListItem4ByecodeUi } from '@lighthouse/shared'
import React, { useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { RenderLabel } from './MultiSelectRenderLabel'

const dateOptions = Object.entries(dateRangeVariableSystemNameMap).map(([key, value]) => ({
    label: value,
    value: key
}))
const emptyDateVariables: string[] = []
const getStringArrayValue = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : emptyDateVariables

interface DateFilterProps {
    prefixName: string
}

export const DateFilter: React.FC<DateFilterProps> = ({ prefixName }) => {
    const { control, setValue, watch } = useFormContext()
    const watchedDateVariables = watch(`${prefixName}.dateVariables`)
    const watchedDateDefaultVariables = watch(`${prefixName}.dateDefaultVariables`)
    const dateVariables = getStringArrayValue(watchedDateVariables)
    const dateDefaultVariables = getStringArrayValue(watchedDateDefaultVariables)
    const selectWay = watch(`${prefixName}.selectWay`)

    const defaultDateOptions = useMemo(() => {
        return dateOptions.filter(item => dateVariables.includes(item.value))
    }, [dateVariables])

    useEffect(() => {
        const validValues = dateDefaultVariables.filter(item => dateVariables.includes(item))
        const nextValues = selectWay === FilterSelectWay.singleSelect ? validValues.slice(0, 1) : validValues
        const hasChanged =
            !Array.isArray(watchedDateDefaultVariables) ||
            nextValues.length !== watchedDateDefaultVariables.length ||
            nextValues.some((item, index) => item !== watchedDateDefaultVariables[index])

        if (!hasChanged) {
            return
        }

        setValue(`${prefixName}.dateDefaultVariables`, nextValues)
    }, [dateDefaultVariables, dateVariables, prefixName, selectWay, setValue, watchedDateDefaultVariables])

    return (
        <>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>预设时间选项</Text>
                <Controller
                    control={control}
                    name={`${prefixName}.dateVariables`}
                    render={({ field: { value, onChange } }) => {
                        const selectedValues = getStringArrayValue(value)

                        return (
                            <MultiSelect
                                dropdownWidth={220}
                                position="bottom-end"
                                styles={{ root: { width: 180 } }}
                                placeholder="请选择"
                                options={dateOptions}
                                value={selectedValues}
                                customInputValueRender={RenderLabel}
                                onChange={val => onChange(val)}
                            />
                        )
                    }}
                />
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>默认值</Text>
                <Controller
                    control={control}
                    name={`${prefixName}.dateDefaultVariables`}
                    render={({ field: { value, onChange } }) => {
                        const selectedValues = getStringArrayValue(value)

                        if (selectWay === FilterSelectWay.singleSelect) {
                            return (
                                <Select
                                    dropdownWidth={220}
                                    position="bottom-end"
                                    styles={{ root: { width: 180 } }}
                                    placeholder="请选择"
                                    options={defaultDateOptions}
                                    value={selectedValues[0]}
                                    onChange={val => onChange(val ? [val] : [])}
                                />
                            )
                        }

                        return (
                            <MultiSelect
                                dropdownWidth={220}
                                position="bottom-end"
                                styles={{ root: { width: 180 } }}
                                placeholder="请选择"
                                options={defaultDateOptions}
                                value={selectedValues}
                                customInputValueRender={RenderLabel}
                                onChange={val => onChange(val)}
                            />
                        )
                    }}
                />
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>自定义时间范围</Text>
                <Controller
                    control={control}
                    name={`${prefixName}.isDateRange`}
                    render={({ field: { value, onChange } }) => {
                        return <Switch checked={value} onChange={onChange} />
                    }}
                />
            </ListItem4ByecodeUi>
        </>
    )
}
